import { Box, Col, FormGroup, FormParagraph, FormSection, Radio, Row } from '@ksyos/design-system';
import { useEffect } from 'react';
import type { YesOrNoOrStoppedOrUnknown } from '@common/enums';
import type { IComorbidities } from '@common/interfaces/comorbidities';
import type { OsasConsultData } from '@common/validation/customControls/osas/osasConsultSchemas/types';
import type { IOsasReferralData } from '@common/validation/customControls/osas/osasReferralSchemas/osasReferralSchema';
import type { IOption } from '@frontend/components/interfaces';
import type { ValidationErrors } from '@frontend/utils';
import { BMICategories, CustomControlCode, MeasureOfCategory, ServiceCode, YesOrNoOrOther } from '@common/enums';
import { ComorbiditiesChoice } from '@common/enums/comorbiditiesChoice';
import { getBmiCategorie, getCalculatedBmiAsNumber } from '@common/utils/bmi';
import { getListFromCheckboxAndText } from '@common/utils/getListFromCheckboxAndText';
import { findSingleProcessByServiceCode, getCustomControlData } from '@common/utils/processUtil';
import { osasReferralComorbiditiesLabels } from '@common/validation/customControls/osas/osasReferralSchemas/osasReferralSchema';
import { AlcoholConsumptionFormGroup } from '@frontend/components/pages/episodeCategory/customControls/osas/osasReferral/components/AlcoholConsumptionFormGroup';
import {
    RadioGroupFormGroup,
    SelectFormGroup,
    TextAreaFormGroup,
} from '@frontend/components/pages/episodeCategory/customControls/processActivityFormGroupFields';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { DrugUseFormGroup } from '../../osasReferral/components/DrugUseFormGroup';
import { SmokingFormGroup } from '../../osasReferral/components/SmokingFormGroup';
import {
    intoxicationCustomControlInitialData,
    osaNoPolicyInitialData,
    osasNoChosenTreatmentInitialData,
    osasYesChosenTreatmentInitialData,
    osaYesConclusionInitialData,
    osaYesPolicyInitialData,
    therapielandProgramSelectionInitialData,
} from '../osasConsultData';

export type ConclusionFormGroupProps = {
    data: OsasConsultData;
    errors: ValidationErrors;
    onChange: (data: Partial<OsasConsultData>, validateKeys?: string | string[] | undefined) => void;
};

export function ConclusionFormGroup(props: Readonly<ConclusionFormGroupProps>) {
    const { data, errors, onChange } = props;
    const { processes } = usePatientEpisodeCategoryContext();
    const process = findSingleProcessByServiceCode(processes, ServiceCode.OSAS);
    const bmiCategoriesOptions: IOption<string>[] = Object.entries(BMICategories).map(([_label, value]) => ({
        label: value,
        value: value,
    }));

    useEffect(() => {
        if (data.hasOsa === YesOrNoOrOther.yes) {
            preFillFieldsFromPreviousData();
        }
    }, [data.hasOsa, process]);

    useEffect(() => {
        if (data.hasOsa !== YesOrNoOrOther.other) {
            onChange({ hasOsaOtherText: null }, 'hasOsaOtherText');
        }

        if (data.hasOsa === YesOrNoOrOther.other) {
            onChange({ ...therapielandProgramSelectionInitialData }, 'checkedTreatments');
        }

        if (data.hasOsa !== YesOrNoOrOther.no) {
            onChange(
                {
                    ...osaNoPolicyInitialData,
                },
                ['remarkAdviceGeneralPractitionerNoOsa', 'adviceGeneralPractitionerNoOsa', 'remarkNoOsa']
            );
        }

        if (data.hasOsa !== YesOrNoOrOther.yes) {
            onChange(
                {
                    ...osaYesConclusionInitialData,
                    ...osaYesPolicyInitialData,
                    ...osasNoChosenTreatmentInitialData,
                    ...osasYesChosenTreatmentInitialData,
                    ...intoxicationCustomControlInitialData,
                },
                [
                    'alcoholStoppedSince',
                    'additionalConclusionChoice',
                    'additionalConclusionText',
                    'bmiCategory',
                    'comorbidities',
                    'comorbiditiesFromReferral',
                    'comorbidityChoice',
                    'daysAWeek',
                    'drugUseStoppedSince',
                    'glassesADay',
                    'measureOfDiseaseBurden',
                    'measureOfOsa',
                    'hasTreatment',
                    'isAlcoholUser',
                    'isDrugUser',
                    'isDrugUserRemark',
                    'isSmoker',
                    'isSmokerRemark',
                    'isDrivingCompetenceDiscussed',
                    'remarkIsDrivingCompetenceDiscussed',
                    'remarkOther',
                    'callPatientAfterWeeks',
                    'messageToGeneralPractitioner',
                    'packsADaySmoked',
                    'remarkNoTreatment',
                    'shouldNotifyGeneralPractitioner',
                    'smokingStoppedSince',
                    'cpapSetting',
                    'cpapSettingText',
                    'refer',
                    'remarkTreatment',
                    'resendQuestionnaires',
                    'resendQuestionnairesWeeks',
                    'taskForPoliclinic',
                    'treatment',
                    'treatmentFollowUp',
                    'yearsSmoked',
                ]
            );
        }
    }, [data.hasOsa]);

    useEffect(() => {
        if (data.comorbidityChoice === ComorbiditiesChoice.nee) {
            onChange({ comorbidities: null }, 'comorbidities');
        }
    }, [data.comorbidityChoice]);

    useEffect(() => {
        if (data.additionalConclusionChoice === YesOrNoOrOther.no) {
            onChange({ additionalConclusionText: null }, 'additionalConclusionText');
        }
    }, [data.additionalConclusionChoice]);

    return (
        <FormSection title="Conclusie">
            <RadioGroupFormGroup
                fieldKey="hasOsa"
                isRequired={true}
                label="Obstructieve Slaapapneu (OSA)"
                onChange={async (value) => {
                    onChange({ hasOsa: value as YesOrNoOrOther }, 'hasOsa');
                }}
                radioGroupLayout="row"
                value={(data.hasOsa as YesOrNoOrOther) ?? undefined}
            >
                <Radio
                    key={YesOrNoOrOther.yes}
                    label="Ja"
                    value={YesOrNoOrOther.yes}
                />
                <Radio
                    key={YesOrNoOrOther.no}
                    label="Nee"
                    value={YesOrNoOrOther.no}
                />
                <Radio
                    key={YesOrNoOrOther.other}
                    label="Anders"
                    value={YesOrNoOrOther.other}
                />
            </RadioGroupFormGroup>
            {data.hasOsa === YesOrNoOrOther.other ? (
                <TextAreaFormGroup
                    fieldKey="hasOsaOtherText"
                    isRequired={true}
                    label="Andere reden"
                />
            ) : null}
            {data.hasOsa === YesOrNoOrOther.yes ? (
                <>
                    <RadioGroupFormGroup
                        fieldKey="measureOfOsa"
                        isRequired={true}
                        label="Mate van OSA"
                        onChange={async (value) => {
                            onChange({ measureOfOsa: value as MeasureOfCategory }, 'measureOfOsa');
                        }}
                        value={(data.measureOfOsa as MeasureOfCategory) ?? undefined}
                    >
                        <Radio
                            key={MeasureOfCategory.light}
                            label="Licht"
                            value={MeasureOfCategory.light}
                        />
                        <Radio
                            key={MeasureOfCategory.moderate}
                            label="Matig"
                            value={MeasureOfCategory.moderate}
                        />
                        <Radio
                            key={MeasureOfCategory.severe}
                            label="Ernstig"
                            value={MeasureOfCategory.severe}
                        />
                    </RadioGroupFormGroup>
                    <RadioGroupFormGroup
                        fieldKey="measureOfDiseaseBurden"
                        isRequired={true}
                        label="Mate van ziektelast"
                        onChange={async (value) => {
                            onChange({ measureOfDiseaseBurden: value as MeasureOfCategory }, 'measureOfDiseaseBurden');
                        }}
                        value={(data.measureOfDiseaseBurden as MeasureOfCategory) ?? undefined}
                    >
                        <Radio
                            key={MeasureOfCategory.none}
                            label="Geen"
                            value={MeasureOfCategory.none}
                        />
                        <Radio
                            key={MeasureOfCategory.light}
                            label="Licht"
                            value={MeasureOfCategory.light}
                        />
                        <Radio
                            key={MeasureOfCategory.moderate}
                            label="Matig"
                            value={MeasureOfCategory.moderate}
                        />
                        <Radio
                            key={MeasureOfCategory.severe}
                            label="Ernstig"
                            value={MeasureOfCategory.severe}
                        />
                    </RadioGroupFormGroup>
                    <SelectFormGroup
                        error={errors.get('bmiCategory')}
                        fieldKey="bmiCategory"
                        isClearable={false}
                        isRequired={true}
                        label="BMI categorie"
                        onChange={handleChangeBMI}
                        options={bmiCategoriesOptions}
                        placeholder="Selecteer BMI categorie"
                        value={data.bmiCategory ?? undefined}
                    />
                    <AlcoholConsumptionFormGroup
                        data={data}
                        errors={errors}
                        onChange={onChange}
                    />
                    <SmokingFormGroup
                        data={data}
                        errors={errors}
                        onChange={onChange}
                    />
                    <DrugUseFormGroup
                        data={data}
                        errors={errors}
                        onChange={onChange}
                    />
                    <FormGroup label="Comorbiditeiten volgens verwijzing">
                        <Row columnGap={8}>
                            <Col>
                                <FormParagraph>
                                    {data.comorbiditiesFromReferral &&
                                    Object.values(data.comorbiditiesFromReferral).some((value) => !!value)
                                        ? getListFromCheckboxAndText(
                                              data.comorbiditiesFromReferral,
                                              osasReferralComorbiditiesLabels
                                          ).join(', ')
                                        : 'geen'}
                                </FormParagraph>
                            </Col>
                        </Row>
                    </FormGroup>
                    <Box marginBottom={4}>
                        <RadioGroupFormGroup
                            fieldKey="comorbidityChoice"
                            isRequired={true}
                            label="Aanvullende comorbiditeiten"
                            onChange={async (value) => {
                                onChange({ comorbidityChoice: value as ComorbiditiesChoice }, 'comorbidityChoice');
                            }}
                            radioGroupLayout="row"
                            value={data?.comorbidityChoice ?? undefined}
                        >
                            <Radio
                                key={ComorbiditiesChoice.ja}
                                label="Ja"
                                value={ComorbiditiesChoice.ja}
                            />
                            <Radio
                                key={ComorbiditiesChoice.nee}
                                label="Nee"
                                value={ComorbiditiesChoice.nee}
                            />
                        </RadioGroupFormGroup>
                    </Box>
                    {data.comorbidityChoice === ComorbiditiesChoice.ja ? (
                        <TextAreaFormGroup
                            fieldKey="comorbidities"
                            isRequired={true}
                            label="Toelichting"
                        />
                    ) : null}
                    <RadioGroupFormGroup
                        fieldKey="additionalConclusionChoice"
                        isRequired={true}
                        label="Aanvullende conclusie"
                        onChange={async (value) => {
                            onChange(
                                { additionalConclusionChoice: value as YesOrNoOrOther },
                                'additionalConclusionChoice'
                            );
                        }}
                        radioGroupLayout="row"
                        value={(data.additionalConclusionChoice as YesOrNoOrOther) ?? undefined}
                    >
                        <Radio
                            key={YesOrNoOrOther.yes}
                            label="Ja"
                            value={YesOrNoOrOther.yes}
                        />
                        <Radio
                            key={YesOrNoOrOther.no}
                            label="Nee"
                            value={YesOrNoOrOther.no}
                        />
                    </RadioGroupFormGroup>
                    {data.additionalConclusionChoice === YesOrNoOrOther.yes ? (
                        <TextAreaFormGroup
                            fieldKey="additionalConclusionText"
                            isRequired={true}
                            label="Toelichting"
                        />
                    ) : null}
                </>
            ) : null}
        </FormSection>
    );

    function handleChangeBMI(value: string | null) {
        const bmiCategory = bmiCategoriesOptions.find((option: IOption<string>) => option.value === value);
        onChange({ bmiCategory: (bmiCategory?.value as BMICategories) || null }, 'bmiCategory');
    }

    function preFillFieldsFromPreviousData() {
        if (process) {
            const referralActivity = getCustomControlData<IOsasReferralData>(process, CustomControlCode.OSASReferral);
            const lifeStyleForm = getCustomControlData<IOsasReferralData>(process, CustomControlCode.OSASLifestyleForm);

            let alcoholStoppedSince: string | null = null;
            let bmiCategoryFromReferral: BMICategories | null = null;
            let comorbiditiesFromReferral: IComorbidities | null = null;
            let daysAWeek: number | null = null;
            let drugUseStoppedSince: string | null = null;
            let glassesADay: number | null = null;
            let isAlcoholUser: YesOrNoOrStoppedOrUnknown | null = null;
            let isDrugUser: YesOrNoOrStoppedOrUnknown | null = null;
            let isDrugUserRemark: string | null = null;
            let isSmoker: YesOrNoOrStoppedOrUnknown | null = null;
            let isSmokerRemark: string | null = null;
            let packsADaySmoked: number | null = null;
            let smokingStoppedSince: string | null = null;
            let yearsSmoked: number | null = null;

            if (referralActivity) {
                const bmiNumber = getCalculatedBmiAsNumber(referralActivity.weightInKg, referralActivity.lengthInCm);
                if (bmiNumber) {
                    bmiCategoryFromReferral = getBmiCategorie(bmiNumber) as BMICategories;
                }
                alcoholStoppedSince = referralActivity.alcoholStoppedSince;
                comorbiditiesFromReferral = referralActivity.comorbidities;
                daysAWeek = referralActivity.daysAWeek === 0 ? null : referralActivity.daysAWeek;
                drugUseStoppedSince = referralActivity.drugUseStoppedSince;
                glassesADay = referralActivity.glassesADay === 0 ? null : referralActivity.glassesADay;
                isAlcoholUser = referralActivity.isAlcoholUser;
                isDrugUser = referralActivity.isDrugUser;
                isDrugUserRemark = referralActivity.isDrugUserRemark;
                isSmoker = referralActivity.isSmoker;
                isSmokerRemark = referralActivity.isSmokerRemark;
                packsADaySmoked = referralActivity.packsADaySmoked;
                smokingStoppedSince = referralActivity.smokingStoppedSince;
                yearsSmoked = referralActivity.yearsSmoked;
            } else if (lifeStyleForm) {
                const bmiNumber = getCalculatedBmiAsNumber(lifeStyleForm.weightInKg, lifeStyleForm.lengthInCm);
                if (bmiNumber) {
                    bmiCategoryFromReferral = getBmiCategorie(bmiNumber) as BMICategories;
                }
                alcoholStoppedSince = lifeStyleForm.alcoholStoppedSince;
                daysAWeek = lifeStyleForm.daysAWeek === 0 ? null : lifeStyleForm.daysAWeek;
                drugUseStoppedSince = lifeStyleForm.drugUseStoppedSince;
                glassesADay = lifeStyleForm.glassesADay === 0 ? null : lifeStyleForm.glassesADay;
                isAlcoholUser = lifeStyleForm.isAlcoholUser;
                isDrugUser = lifeStyleForm.isDrugUser;
                isDrugUserRemark = lifeStyleForm.isDrugUserRemark;
                isSmoker = lifeStyleForm.isSmoker;
                isSmokerRemark = lifeStyleForm.isSmokerRemark;
                packsADaySmoked = lifeStyleForm.packsADaySmoked;
                smokingStoppedSince = lifeStyleForm.smokingStoppedSince;
                yearsSmoked = lifeStyleForm.yearsSmoked;
            }

            onChange(
                {
                    alcoholStoppedSince: data.alcoholStoppedSince ?? alcoholStoppedSince,
                    bmiCategory: data.bmiCategory ?? bmiCategoryFromReferral,
                    comorbiditiesFromReferral: comorbiditiesFromReferral,
                    daysAWeek: data.daysAWeek || daysAWeek,
                    drugUseStoppedSince: data.drugUseStoppedSince ?? drugUseStoppedSince,
                    glassesADay: data.glassesADay || glassesADay,
                    isAlcoholUser: data.isAlcoholUser ?? isAlcoholUser,
                    isDrugUser: data.isDrugUser ?? isDrugUser,
                    isDrugUserRemark: data.isDrugUserRemark ?? isDrugUserRemark,
                    isSmoker: data.isSmoker ?? isSmoker,
                    isSmokerRemark: data.isSmokerRemark ?? isSmokerRemark,
                    packsADaySmoked: data.packsADaySmoked ?? packsADaySmoked,
                    smokingStoppedSince: data.smokingStoppedSince ?? smokingStoppedSince,
                    yearsSmoked: data.yearsSmoked ?? yearsSmoked,
                },
                []
            );
        }
    }
}
